import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

interface RoleBasedRouteProps {
  roleComponents: {
    [key: string]: React.ReactNode;
  };
  defaultFallback?: string;
}

/**
 * Component that renders different content based on the user's current role.
 * Ensures only the correct role's component is rendered, with safety checks.
 */
const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ roleComponents, defaultFallback = '/login-page' }) => {
  const currentUser = useSelector((state: any) => state.users?.currentUser);
  
  // REINFORCEMENT: If Redux doesn't have user, check cache before redirecting
  // This prevents auth state flicker from causing unexpected logouts
  if (!currentUser || !currentUser.selectedAffiliation) {
    try {
      const cachedAuth = localStorage.getItem('cachedAuth');
      if (cachedAuth) {
        const parsed = JSON.parse(cachedAuth);
        if (parsed && parsed.uid) {
          // Valid cached auth exists - this is likely an auth flicker
          // Render nothing while the auth state stabilizes (App.tsx will restore)
          console.log('RoleBasedRoute: No Redux user but cached auth exists. Waiting for auth restoration...');
          return null; // Render nothing, don't redirect
        }
      }
    } catch (e) {
      // Parsing failed, proceed with redirect
    }
    return <Navigate to={defaultFallback} replace />;
  }

  const selectedRole = currentUser.selectedAffiliation.role;
  
  // Check if there's a component for this role
  if (roleComponents[selectedRole]) {
    return <>{roleComponents[selectedRole]}</>;
  }

  // No component defined for this role, redirect to their dashboard
  console.warn(`RoleBasedRoute - No component defined for role "${selectedRole}"`);
  switch (selectedRole) {
    case 'admin':
      return <Navigate to="/admin-dashboard" replace />;
    case 'guardian':
      return <Navigate to="/guardian-dashboard" replace />;
    case 'school_staff':
      return <Navigate to="/faculty-dashboard" replace />;
    case 'student':
      return <Navigate to="/student-dashboard" replace />;
    case 'owner':
      return <Navigate to="/owner-dashboard" replace />;
    default:
      return <Navigate to={defaultFallback} replace />;
  }
};

export default RoleBasedRoute;
